import { useState, useEffect, useCallback } from 'react';
import { Play, Pause, Square, Clock, Euro, WifiOff } from '../../icons/wp-icons';
import { zeiterfassungApi } from '../../api/zeiterfassungApi';
import apiClient from '../../api/apiClient';
import {
  cacheMultiple,
  getAllCachedData,
  setMetadata,
  isCacheValid,
  STORES
} from '../../utils/offlineDB';

function TimerWidget({ terminId, onUpdate }) {
  const [katalogPositionen, setKatalogPositionen] = useState([]);
  const [selectedKatalogId, setSelectedKatalogId] = useState('');
  const [notiz, setNotiz] = useState('');
  const [laufendeZeit, setLaufendeZeit] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Online/Offline Status tracken
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Katalogpositionen laden (mit Offline-Cache)
  useEffect(() => {
    const loadKatalog = async () => {
      try {
        // Wenn online, vom Server laden und cachen
        if (navigator.onLine) {
          const response = await apiClient.get('/katalog', {
            params: { einheit: 'Std', aktiv: true }
          });
          // Filtern nach Einheit "Std"
          const stdPositionen = (response.data || []).filter(
            pos => pos.einheit === 'Std' && pos.aktiv
          );
          setKatalogPositionen(stdPositionen);

          // In Cache speichern
          await cacheMultiple(STORES.CACHE_KATALOG, stdPositionen);
          await setMetadata(`${STORES.CACHE_KATALOG}-last-sync`, Date.now());

          if (stdPositionen.length > 0 && !selectedKatalogId) {
            setSelectedKatalogId(stdPositionen[0].id);
          }
        } else {
          // Offline: Aus Cache laden
          console.log('📦 Katalog aus Cache laden (offline)');
          const cached = await getAllCachedData(STORES.CACHE_KATALOG);
          // Nur Std-basierte und aktive
          const stdPositionen = cached.filter(
            pos => pos.einheit === 'Std' && pos.aktiv
          );
          setKatalogPositionen(stdPositionen);

          if (stdPositionen.length > 0 && !selectedKatalogId) {
            setSelectedKatalogId(stdPositionen[0].id);
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden des Katalogs:', err);

        // Bei Fehler: Versuche aus Cache zu laden
        try {
          const cached = await getAllCachedData(STORES.CACHE_KATALOG);
          const stdPositionen = cached.filter(
            pos => pos.einheit === 'Std' && pos.aktiv
          );
          if (stdPositionen.length > 0) {
            console.log('📦 Katalog aus Cache geladen (Fehler-Fallback)');
            setKatalogPositionen(stdPositionen);
            if (!selectedKatalogId) {
              setSelectedKatalogId(stdPositionen[0].id);
            }
          }
        } catch (cacheErr) {
          console.error('Cache-Fehler:', cacheErr);
        }
      }
    };
    loadKatalog();
  }, []);

  // Aktiven Timer laden
  const loadAktiverTimer = useCallback(async () => {
    // Nur laden wenn online (offline-Zeiterfassung wird lokal gestartet)
    if (!navigator.onLine) return;

    try {
      const data = await zeiterfassungApi.getAktiverTimer(terminId);
      if (data.laufenderTimer) {
        setLaufendeZeit(data.laufenderTimer);
        if (data.laufenderTimer.katalogPositionId) {
          setSelectedKatalogId(data.laufenderTimer.katalogPositionId);
        }
        setNotiz(data.laufenderTimer.notiz || '');
      } else {
        setLaufendeZeit(null);
      }
    } catch (err) {
      console.error('Fehler beim Laden des aktiven Timers:', err);
    }
  }, [terminId]);

  useEffect(() => {
    loadAktiverTimer();
  }, [loadAktiverTimer]);

  // Timer-Logik: Live-Update der verstrichenen Zeit
  useEffect(() => {
    let interval;
    if (laufendeZeit && laufendeZeit.status === 'laufend') {
      interval = setInterval(() => {
        const start = new Date(laufendeZeit.startzeit);
        const now = new Date();

        // Pausenzeit berechnen und abziehen
        let pausenMs = 0;
        if (Array.isArray(laufendeZeit.pausen)) {
          for (const pause of laufendeZeit.pausen) {
            if (pause.start && pause.ende) {
              pausenMs += new Date(pause.ende) - new Date(pause.start);
            }
          }
        }

        const totalMs = (now - start) - pausenMs;
        const totalSeconds = Math.max(0, Math.floor(totalMs / 1000));
        setTimerSeconds(totalSeconds);
      }, 1000);
    } else if (laufendeZeit && laufendeZeit.status === 'pausiert') {
      // Bei Pause: Zeit bis zur letzten Pause berechnen
      const start = new Date(laufendeZeit.startzeit);
      let endTime = new Date();

      // Letzte Pause (offen)
      if (Array.isArray(laufendeZeit.pausen) && laufendeZeit.pausen.length > 0) {
        const letztePause = laufendeZeit.pausen[laufendeZeit.pausen.length - 1];
        if (letztePause.start && !letztePause.ende) {
          endTime = new Date(letztePause.start);
        }
      }

      // Abgeschlossene Pausenzeit
      let pausenMs = 0;
      if (Array.isArray(laufendeZeit.pausen)) {
        for (const pause of laufendeZeit.pausen) {
          if (pause.start && pause.ende) {
            pausenMs += new Date(pause.ende) - new Date(pause.start);
          }
        }
      }

      const totalMs = (endTime - start) - pausenMs;
      const totalSeconds = Math.max(0, Math.floor(totalMs / 1000));
      setTimerSeconds(totalSeconds);
    } else {
      setTimerSeconds(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [laufendeZeit]);

  // Timer formatieren (HH:MM:SS)
  const formatTimer = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Stundensatz der ausgewählten Position
  const getStundensatz = () => {
    const pos = katalogPositionen.find(p => p.id === selectedKatalogId);
    return pos ? pos.verkaufspreis : 0;
  };

  // Bezeichnung der ausgewählten Position
  const getBezeichnung = () => {
    const pos = katalogPositionen.find(p => p.id === selectedKatalogId);
    return pos ? pos.bezeichnung : 'Arbeitszeit';
  };

  // Timer starten
  const handleStart = async () => {
    if (!selectedKatalogId) {
      setError('Bitte wähle eine Tätigkeit aus');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await zeiterfassungApi.start({
        terminId,
        katalogPositionId: selectedKatalogId,
        notiz: notiz || null
      });

      // Wenn offline gespeichert wurde
      if (data.offline) {
        // Lokalen Timer starten für UI
        setLaufendeZeit({
          ...data.zeiterfassung,
          stundensatz: getStundensatz(),
          katalogPosition: { bezeichnung: getBezeichnung() }
        });
      } else {
        setLaufendeZeit(data.zeiterfassung);
      }

      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Starten des Timers');
    } finally {
      setLoading(false);
    }
  };

  // Timer pausieren
  const handlePause = async () => {
    if (!laufendeZeit) return;

    // Offline: Pause nicht unterstützt
    if (!navigator.onLine || laufendeZeit.offline) {
      setError('Pause ist im Offline-Modus nicht verfügbar');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await zeiterfassungApi.pause(laufendeZeit.id);
      setLaufendeZeit(data.zeiterfassung);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Pausieren');
    } finally {
      setLoading(false);
    }
  };

  // Timer fortsetzen
  const handleResume = async () => {
    if (!laufendeZeit) return;

    // Offline: Resume nicht unterstützt
    if (!navigator.onLine || laufendeZeit.offline) {
      setError('Fortsetzen ist im Offline-Modus nicht verfügbar');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await zeiterfassungApi.resume(laufendeZeit.id);
      setLaufendeZeit(data.zeiterfassung);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Fortsetzen');
    } finally {
      setLoading(false);
    }
  };

  // Timer stoppen
  const handleStop = async () => {
    if (!laufendeZeit) return;

    setLoading(true);
    setError('');
    try {
      // Offline-Timer: Speichere komplette Zeiterfassung für späteren Sync
      if (laufendeZeit.offline || (laufendeZeit.id && laufendeZeit.id.startsWith('offline-'))) {
        // Übergebe Timer-Daten inkl. Stundensatz
        const timerDataWithStundensatz = {
          ...laufendeZeit,
          stundensatz: getStundensatz()
        };

        const data = await zeiterfassungApi.stop(laufendeZeit.id, timerDataWithStundensatz);

        // Zusammenfassung anzeigen
        alert(
          `Timer gestoppt!\n\n` +
          `Arbeitszeit: ${data.zusammenfassung.arbeitszeit}\n` +
          `Stundensatz: ${data.zusammenfassung.stundensatz.toFixed(2)} €/Std\n` +
          `Kosten: ${data.zusammenfassung.kostenFormatiert}\n\n` +
          `(Wird synchronisiert sobald online)`
        );

        setLaufendeZeit(null);
        setTimerSeconds(0);
        if (onUpdate) onUpdate();
        return;
      }

      const data = await zeiterfassungApi.stop(laufendeZeit.id);
      setLaufendeZeit(null);
      setTimerSeconds(0);

      // Zusammenfassung anzeigen
      alert(
        `Timer gestoppt!\n\n` +
        `Arbeitszeit: ${data.zusammenfassung.arbeitszeit}\n` +
        `Pausenzeit: ${data.zusammenfassung.pausenzeit}\n` +
        `Stundensatz: ${data.zusammenfassung.stundensatz.toFixed(2)} €/Std\n` +
        `Kosten: ${data.zusammenfassung.kostenFormatiert}`
      );

      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Stoppen');
    } finally {
      setLoading(false);
    }
  };

  // Anzahl der abgeschlossenen Pausen
  const getPausenAnzahl = () => {
    if (!laufendeZeit?.pausen) return 0;
    return laufendeZeit.pausen.filter(p => p.start && p.ende).length;
  };

  const isRunning = laufendeZeit?.status === 'laufend';
  const isPaused = laufendeZeit?.status === 'pausiert';
  const hasActiveTimer = isRunning || isPaused;

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      {/* Offline-Hinweis */}
      {isOffline && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 text-orange-700 rounded-lg text-sm flex items-center gap-2">
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span>Offline-Modus: Zeiterfassung wird lokal gespeichert</span>
        </div>
      )}

      {/* Fehler */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Kein laufender Timer - Auswahl anzeigen */}
      {!hasActiveTimer && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tätigkeit auswählen
            </label>
            <select
              value={selectedKatalogId}
              onChange={(e) => setSelectedKatalogId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">-- Tätigkeit wählen --</option>
              {katalogPositionen.map((pos) => (
                <option key={pos.id} value={pos.id}>
                  {pos.bezeichnung} ({pos.verkaufspreis.toFixed(2)} €/Std)
                </option>
              ))}
            </select>
            {katalogPositionen.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {isOffline ? 'Keine Tätigkeiten im Cache verfügbar' : 'Lädt...'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notiz (optional)
            </label>
            <input
              type="text"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="z.B. Fensterreparatur Küche"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {selectedKatalogId && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Euro className="w-4 h-4" />
              <span>Stundensatz: {getStundensatz().toFixed(2)} €/Std</span>
            </div>
          )}

          <button
            onClick={handleStart}
            disabled={loading || !selectedKatalogId}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play className="w-5 h-5" />
            Timer starten
          </button>
        </div>
      )}

      {/* Laufender/Pausierter Timer */}
      {hasActiveTimer && (
        <div className="space-y-4">
          {/* Timer-Anzeige */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className={`w-6 h-6 ${isRunning ? 'text-green-600 animate-pulse' : 'text-yellow-600'}`} />
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                isRunning ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {isRunning ? 'Läuft' : 'Pausiert'}
              </span>
              {laufendeZeit.offline && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                  Offline
                </span>
              )}
            </div>

            <div className="text-4xl font-mono font-bold text-gray-900">
              {formatTimer(timerSeconds)}
            </div>

            {/* Tätigkeit & Stundensatz */}
            <div className="mt-2 text-sm text-gray-600">
              {laufendeZeit.katalogPosition?.bezeichnung || getBezeichnung() || 'Arbeitszeit'}
              <span className="mx-2">•</span>
              {(laufendeZeit.stundensatz || getStundensatz()).toFixed(2)} €/Std
            </div>

            {/* Pausen */}
            {getPausenAnzahl() > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                {getPausenAnzahl()} Pause(n)
              </div>
            )}
          </div>

          {/* Notiz (falls vorhanden) */}
          {laufendeZeit.notiz && (
            <div className="text-sm text-gray-600 bg-white rounded-lg p-2 text-center">
              {laufendeZeit.notiz}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            {isRunning ? (
              <button
                onClick={handlePause}
                disabled={loading || laufendeZeit.offline}
                className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-yellow-600 transition disabled:opacity-50"
                title={laufendeZeit.offline ? 'Im Offline-Modus nicht verfügbar' : ''}
              >
                <Pause className="w-5 h-5" />
                Pause
              </button>
            ) : (
              <button
                onClick={handleResume}
                disabled={loading || laufendeZeit.offline}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50"
                title={laufendeZeit.offline ? 'Im Offline-Modus nicht verfügbar' : ''}
              >
                <Play className="w-5 h-5" />
                Fortsetzen
              </button>
            )}

            <button
              onClick={handleStop}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50"
            >
              <Square className="w-5 h-5" />
              Stoppen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimerWidget;
